import { LinkIcon, UserIcon } from "@heroicons/react/outline";
import cn from "classnames";
import currency from "currency.js";
import React from "react";
import { Link } from "react-router-dom";
import { useTitle } from "react-use";
import { ROUTE_SETTINGS_CONTACT, ROUTE_SETTINGS_FINANCIAL } from "../../routes";
import { calcAnnuityBreakdown } from "../../utils/calculations";
import { CalculatorGraph } from "../Calculator/Calculator";

const actions = [
  {
    icon: UserIcon,
    name: "Complete Contact Information",
    to: ROUTE_SETTINGS_CONTACT,
    iconForeground: "text-purple-700",
    iconBackground: "bg-purple-50",
    text: "",
  },
  {
    icon: LinkIcon,
    name: "Complete Financial Background",
    to: ROUTE_SETTINGS_FINANCIAL,
    iconForeground: "text-light-blue-700",
    iconBackground: "bg-light-blue-50",
    text: "",
  },
  // {
  //   icon: ShieldCheckIcon,
  //   name: "Set Up Two Factor Auth",
  //   href: "#",
  //   iconForeground: "text-yellow-700",
  //   iconBackground: "bg-yellow-50",
  //   text: "",
  // },
  // {
  //   icon: ArrowCircleUpIcon,
  //   name: "Retirement Tools",
  //   href: "#",
  //   iconForeground: "text-teal-700",
  //   iconBackground: "bg-teal-50",
  //   text: "",
  // },
];

const announcements = [
  {
    id: 1,
    title: "Coming Soon to Utah",
    href: "#",
    preview: "We are working hard on providing the best annuity plans.",
  },
];

const { yearlyBreakdown, monthlyPayout } = calcAnnuityBreakdown(
  3,
  35,
  65,
  0,
  10000,
  2150
);

const Home = () => {
  useTitle("Home | Due");
  return (
    <>
      <h1 className="sr-only">Profile</h1>
      <div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8">
        <div className="grid grid-cols-1 gap-4 lg:col-span-2">
          <section className="rounded-lg bg-white shadow">
            <div className="p-8">
              <div className="flex mb-4">
                <div>
                  <div className="text-3xl">
                    {currency(
                      yearlyBreakdown.find((data) => data.age === 65)
                        ?.balance || 100
                    ).format()}
                  </div>
                  <div className="text-sm text-gray-500">Balance at {65}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-3xl">
                    {currency(monthlyPayout).format()}
                  </div>
                  <div className="text-sm text-gray-500">
                    Monthly Payouts at 65
                  </div>
                </div>
              </div>
              <CalculatorGraph data={yearlyBreakdown} height={300} />
              <div className="text-xs text-gray-400 text-right">
                * Not actual amounts. Demo amounts for display purposes.
              </div>
            </div>
          </section>
          <section aria-labelledby="quick-links-title">
            <div className="rounded-lg bg-gray-200 overflow-hidden shadow divide-y divide-gray-200 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px">
              <h2 className="sr-only" id="quick-links-title">
                Quick links
              </h2>
              {actions.map((action, actionIdx) => (
                <div
                  key={action.name}
                  className={cn(
                    actionIdx === 0
                      ? "rounded-tl-lg rounded-tr-lg sm:rounded-tr-none"
                      : "",
                    actionIdx === 1 ? "sm:rounded-tr-lg" : "",
                    actionIdx === actions.length - 2 ? "sm:rounded-bl-lg" : "",
                    actionIdx === actions.length - 1
                      ? "rounded-bl-lg rounded-br-lg sm:rounded-bl-none"
                      : "",
                    "relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-green-500"
                  )}
                >
                  <div>
                    <span
                      className={cn(
                        action.iconBackground,
                        action.iconForeground,
                        "rounded-lg inline-flex p-3 ring-4 ring-white"
                      )}
                    >
                      <action.icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="mt-8">
                    <h3 className="text-lg font-medium">
                      <Link to={action.to} className="focus:outline-none">
                        {/* Extend touch target to entire panel */}
                        <span className="absolute inset-0" aria-hidden="true" />
                        {action.name}
                      </Link>
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">{action.text}</p>
                  </div>
                  <span
                    className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                    aria-hidden="true"
                  >
                    <svg
                      className="h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                    </svg>
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right column */}
        <div className="grid grid-cols-1 gap-4">
          <section aria-labelledby="announcements-title">
            <div className="rounded-lg bg-white overflow-hidden shadow">
              <div className="p-6">
                <h2
                  className="text-base font-medium text-gray-900"
                  id="announcements-title"
                >
                  Announcements
                </h2>
                <div className="flow-root mt-6">
                  <ul className="-my-5 divide-y divide-gray-200">
                    {announcements.map((announcement) => (
                      <li key={announcement.id} className="py-5">
                        <div className="relative focus-within:ring-2 focus-within:ring-cyan-500">
                          <h3 className="text-sm font-semibold text-gray-800">
                            <a
                              href={announcement.href}
                              className="hover:underline focus:outline-none"
                            >
                              {/* Extend touch target to entire panel */}
                              <span
                                className="absolute inset-0"
                                aria-hidden="true"
                              />
                              {announcement.title}
                            </a>
                          </h3>
                          <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                            {announcement.preview}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-6">
                  <a
                    href="#s"
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    View all
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Home;
